import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  createKanbanState as createKanbanStateApi,
  deleteKanbanState as deleteKanbanStateApi,
  searchKanbanState,
  updateKanbanState as updateKanbanStateApi,
  updateSortIndexes,
  type KanbanStateCreate,
  type KanbanStateInDb,
  type KanbanStateUpdate,
  type KanbanStateUpdateSortIndexes,
  type SearchQuery,
} from '@/api';
import { logger } from '@/utils/logger';

export const useKanbanStore = defineStore('kanban', () => {
  // State
  const kanbanStates = ref<KanbanStateInDb[]>([]);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);

  // Getters
  const sortedKanbanStates = computed<KanbanStateInDb[]>(() => {
    return [...kanbanStates.value].sort((a, b) => a.position - b.position);
  });

  const visibleKanbanStates = computed<KanbanStateInDb[]>(() => {
    return sortedKanbanStates.value.filter(state => !state.hidden);
  });

  /** The columns the board actually draws: visible, and not the archive. */
  const boardColumns = computed<KanbanStateInDb[]>(() => {
    return visibleKanbanStates.value.filter(state => !state.archive_state);
  });

  /** Archived columns — the restorable list in the columns menu. */
  const archivedColumns = computed<KanbanStateInDb[]>(() => {
    return sortedKanbanStates.value.filter(state => state.archive_state);
  });

  // Actions
  async function fetchKanbanStates(): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const query: SearchQuery = {
        text: '', // Empty search text to get all states
        limit: 50, // Assuming we won't have more than 100 states
        sort_key: 'position',
        sort_direction: 'asc',
      };

      const response = await searchKanbanState({ body: query });

      if (!response.data) {
        throw new Error('Failed to fetch kanban states');
      }
      const result = response.data;
      kanbanStates.value = result.results;
    } catch (error_) {
      logger.error('Error fetching kanban states:', error_);
      error.value = error_ instanceof Error ? error_.message : String(error_);
    } finally {
      loading.value = false;
    }
  }

  async function createKanbanState(
    name: string,
    position: number
  ): Promise<KanbanStateInDb | null> {
    loading.value = true;
    error.value = null;

    try {
      const newState: KanbanStateCreate = {
        name,
        position,
        hidden: false,
      };
      const response = await createKanbanStateApi({ body: newState });

      if (!response.data) {
        throw new Error('Failed to create kanban state');
      }
      const result = response.data;
      kanbanStates.value.push(result);
      return result;
    } catch (error_) {
      logger.error('Error creating kanban state:', error_);
      error.value = error_ instanceof Error ? error_.message : String(error_);
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function updateKanbanState(
    uid: string,
    data: KanbanStateUpdate
  ): Promise<KanbanStateInDb | null> {
    loading.value = true;
    error.value = null;

    try {
      const response = await updateKanbanStateApi({
        path: { kanban_state_uid: uid },
        body: data,
      });

      if (!response.data) {
        throw new Error('Failed to update kanban state');
      }
      const result = response.data;

      // Update the state in the local array
      const index = kanbanStates.value.findIndex(state => state.uid === uid);
      if (index !== -1) {
        kanbanStates.value[index] = result;
      }

      return result;
    } catch (error_) {
      logger.error('Error updating kanban state:', error_);
      error.value = error_ instanceof Error ? error_.message : String(error_);
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function toggleKanbanStateVisibility(uid: string): Promise<KanbanStateInDb | null> {
    const state = kanbanStates.value.find((state: KanbanStateInDb) => state.uid === uid);
    if (!state) {
      error.value = 'Kanban state not found';
      return null;
    }

    return updateKanbanState(uid, { hidden: !state.hidden });
  }

  async function deleteKanbanState(uid: string): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      // Check if the state is protected
      const state = kanbanStates.value.find((state: KanbanStateInDb) => state.uid === uid);
      if (state?.protected) {
        throw new Error('Cannot delete a protected kanban state');
      }

      await deleteKanbanStateApi({ path: { kanban_state_uid: uid } });

      // Remove the state from the local array
      kanbanStates.value = kanbanStates.value.filter((state: KanbanStateInDb) => state.uid !== uid);
      return true;
    } catch (error_) {
      logger.error('Error deleting kanban state:', error_);
      error.value = error_ instanceof Error ? error_.message : String(error_);
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function reorderKanbanStates(orderedUids: string[]): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      // Update positions for each state
      const updatePromises = orderedUids.map((uid, index) => {
        return updateKanbanState(uid, { position: index + 1 });
      });

      await Promise.all(updatePromises);
      return true;
    } catch (error_) {
      logger.error('Error reordering kanban states:', error_);
      error.value = error_ instanceof Error ? error_.message : String(error_);
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Updates the sort indexes of reviews in a kanban state
   *
   * @param kanbanStateUid The UUID of the kanban state to update
   * @param reviewUidsToPositions Map of review UIDs to their new positions
   * @returns The updated kanban state
   */
  async function updateKanbanStateSortIndexes(
    kanbanStateUid: string,
    reviewUidsToPositions: Record<string, number>
  ): Promise<KanbanStateInDb | null> {
    loading.value = true;
    error.value = null;

    try {
      const payload: KanbanStateUpdateSortIndexes = {
        review_uids_to_positions: reviewUidsToPositions,
      };

      const response = await updateSortIndexes({
        path: { kanban_state_uid: kanbanStateUid },
        body: payload,
      });

      if (!response.data) {
        throw new Error('Failed to update kanban state sort indexes');
      }
      const result = response.data;

      // Update local state if needed
      const stateIndex = kanbanStates.value.findIndex(state => state.uid === kanbanStateUid);
      if (stateIndex !== -1) {
        kanbanStates.value[stateIndex] = result;
      }

      return result;
    } catch (error_) {
      logger.error('Error updating kanban state sort indexes:', error_);
      error.value = error_ instanceof Error ? error_.message : String(error_);
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Move a review into a column at a position — the one mutation path.
   *
   * `POST /kanban-state/{uid}/update-sort-indexes` writes both
   * `kanban_state_id` and `kanban_column_sorting_position` for every uid in the
   * map (see the backend's `update_sort_indexes`), so this single call both
   * moves and orders. Three call sites used to disagree about that: the board
   * called only this, the details modal called only this, the insight grid
   * called this *and* `updateReview`, and the table called only `updateReview` —
   * which moved the review but left its old sort position behind.
   *
   * Pass `siblings` (the uids already in the target column, in display order)
   * to renumber the whole column in one request; omit it to place the review
   * without touching its new neighbours.
   */
  async function moveReview(
    reviewUid: string,
    targetStateUid: string,
    position = 0,
    siblings?: string[]
  ): Promise<KanbanStateInDb | null> {
    const positions: Record<string, number> = {};

    if (siblings) {
      const ordered = siblings.filter(uid => uid !== reviewUid);
      ordered.splice(Math.max(0, Math.min(position, ordered.length)), 0, reviewUid);
      for (const [index, uid] of ordered.entries()) {
        positions[uid] = index;
      }
    } else {
      positions[reviewUid] = position;
    }

    return updateKanbanStateSortIndexes(targetStateUid, positions);
  }

  return {
    kanbanStates,
    loading,
    error,
    sortedKanbanStates,
    visibleKanbanStates,
    boardColumns,
    archivedColumns,
    fetchKanbanStates,
    createKanbanState,
    updateKanbanState,
    toggleKanbanStateVisibility,
    deleteKanbanState,
    reorderKanbanStates,
    updateKanbanStateSortIndexes,
    moveReview,
  };
});
